import { resolveEventTimezone } from "./zipTimezone";

/**
 * Display helpers for the MSR portal (schedule cards, event header).
 *
 * Times are shown in the EVENT's own timezone (a rep at a Texas gun show sees Central time even if their phone
 * says Eastern), and a multi-day event shows its whole span ("Sat–Sun, Oct 10–11").
 */

type AddressObject = {
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
};

/**
 * One readable line for an event address. location_address arrives as an object (events table), as JSON TEXT
 * (get_msr_schedule casts it to text, which showed `{"state": "NC"}` on the schedule card), or as a plain string.
 */
export function formatEventAddress(addr: unknown): string {
  if (addr === null || addr === undefined) {
    return "";
  }
  let value: unknown = addr;
  if (typeof value === "string") {
    const s = value.trim();
    if (!s) {
      return "";
    }
    if (!s.startsWith("{")) {
      return s;
    }
    try {
      value = JSON.parse(s);
    } catch {
      return "";
    }
  }
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return "";
  }
  const a = value as AddressObject;
  const clean = (v: unknown) =>
    typeof v === "string" && v.trim() !== "" ? v.trim() : null;
  const cityState = [clean(a.city), clean(a.state)].filter(Boolean).join(", ");
  const tail = [cityState, clean(a.postal_code)].filter(Boolean).join(" ");
  return [clean(a.address_line1), clean(a.address_line2), tail]
    .filter(Boolean)
    .join(", ");
}

/** Name + address, joined for a card or header. */
export function formatEventLocation(
  name: string | null | undefined,
  addr: unknown,
): string {
  const parts: string[] = [];
  if (name && name.trim()) {
    parts.push(name.trim());
  }
  const a = formatEventAddress(addr);
  if (a) {
    parts.push(a);
  }
  return parts.join(" · ");
}

function validZone(tz: string | null | undefined): string | undefined {
  if (!tz) {
    return undefined;
  }
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return tz;
  } catch {
    return undefined;
  }
}

function parts(d: Date, tz: string | undefined) {
  const f = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).formatToParts(d);
  const get = (t: string) => f.find((p) => p.type === t)?.value ?? "";
  return {
    weekday: get("weekday"),
    month: get("month"),
    day: get("day"),
    year: get("year"),
  };
}

function timeOf(d: Date, tz: string | undefined): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
}

function zoneName(d: Date, tz: string | undefined): string {
  const z = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    timeZoneName: "short",
  })
    .formatToParts(d)
    .find((p) => p.type === "timeZoneName");
  return z?.value ?? "";
}

/**
 * The event's date/time in its own timezone.
 *   one day:   "Sat, Oct 10 · 9:00 AM – 5:00 PM CDT"
 *   two+ days: "Sat–Sun, Oct 10–11 · 9:00 AM – 5:00 PM CDT"
 *   months:    "Fri, Oct 30 – Mon, Nov 2 · 10:00 PM – 2:00 AM CST"
 * No start = "Date TBD". `dateTbd` wins over a placeholder start.
 */
export function formatEventSpan(
  start: string | null | undefined,
  end: string | null | undefined,
  timezone: string | null | undefined,
  dateTbd = false,
): string {
  if (dateTbd || !start) {
    return "Date TBD";
  }
  const s = new Date(start);
  if (Number.isNaN(s.getTime())) {
    return "Date TBD";
  }
  const tz = validZone(timezone);
  const e = end ? new Date(end) : null;
  const endOk = e !== null && !Number.isNaN(e.getTime()) && e > s;
  const ps = parts(s, tz);
  const zone = zoneName(s, tz);
  if (!endOk) {
    return `${ps.weekday}, ${ps.month} ${ps.day} · ${timeOf(s, tz)}${zone ? ` ${zone}` : ""}`;
  }
  const pe = parts(e as Date, tz);
  const sameDay =
    ps.year === pe.year && ps.month === pe.month && ps.day === pe.day;
  const times = `${timeOf(s, tz)} – ${timeOf(e as Date, tz)}${zone ? ` ${zone}` : ""}`;
  if (sameDay) {
    return `${ps.weekday}, ${ps.month} ${ps.day} · ${times}`;
  }
  if (ps.year === pe.year && ps.month === pe.month) {
    return `${ps.weekday}–${pe.weekday}, ${ps.month} ${ps.day}–${pe.day} · ${times}`;
  }
  return `${ps.weekday}, ${ps.month} ${ps.day} – ${pe.weekday}, ${pe.month} ${pe.day} · ${times}`;
}

/** "Sat, Oct 10" for a YYYY-MM-DD calendar date (no timezone shift: it already is the event's local day). */
export function formatServiceDate(isoDate: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!m) {
    return isoDate;
  }
  const d = new Date(
    Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12),
  );
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(d);
}

/** "Sat, Oct 10 and Sun, Oct 11" / "Sat, Oct 10" — the days a per-day rep works. */
export function formatRepDays(
  days: readonly string[] | null | undefined,
): string {
  if (!days || days.length === 0) {
    return "";
  }
  const labels = [...days].sort().map(formatServiceDate);
  if (labels.length === 1) {
    return labels[0];
  }
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

/** "9:00 AM" in the event's zone ("" when there is no time). Never throws on a bad zone. */
export function formatTimeInZone(
  iso: string | null | undefined,
  timezone: string | null | undefined,
): string {
  if (!iso) {
    return "";
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : timeOf(d, validZone(timezone));
}

/**
 * MSR QA notes 2026-10-19, item 4: reps never pick a time zone. An event's zone comes from its location:
 *   1. the event's stored timezone (the event form sets it from the venue ZIP, EVT-08),
 *   2. else the ZIP / state found in its location text, through the same resolver the event form uses,
 *   3. else America/Chicago (the business default the SQL helpers use).
 */
export interface EventZone {
  timezone: string;
  source: "event" | "zip" | "state" | "default";
}

export function eventZoneFromLocation(row: {
  timezone?: string | null;
  location_address?: unknown;
  location_name?: string | null;
}): EventZone {
  const stored = validZone(row.timezone ?? null);
  if (stored) {
    return { timezone: stored, source: "event" };
  }
  const text = [
    formatEventAddress(row.location_address),
    row.location_name ?? "",
  ]
    .join(" ")
    .toUpperCase();
  const zip = text.match(/\b(\d{5})(?:-\d{4})?\b/)?.[1] ?? null;
  const states = text.match(/\b[A-Z]{2}\b/g) ?? [];
  const state = states.length > 0 ? states[states.length - 1] : null;
  const resolved = resolveEventTimezone(zip, state);
  if (resolved) {
    return { timezone: resolved.timezone, source: resolved.source };
  }
  return { timezone: "America/Chicago", source: "default" };
}

/**
 * A Date whose LOCAL fields are the event-local wall clock of `iso` in `tz`, for calendar grids that lay out by the
 * browser's local time: a 6 PM Central event shows at 6 PM for a rep whose phone is set to Eastern.
 */
export function wallClockDate(iso: string, tz: string): Date {
  const d = new Date(iso);
  const f = new Intl.DateTimeFormat("en-US", {
    timeZone: validZone(tz),
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(d);
  const get = (t: string) => Number(f.find((p) => p.type === t)?.value ?? 0);
  return new Date(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour") % 24,
    get("minute"),
  );
}

/** The short zone label ("CDT") for an instant in a zone. */
export function zoneAbbreviation(iso: string, tz: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : zoneName(d, validZone(tz));
}
